// CERBERUS's client module: the BossView renderer (../boss.ts) plus the bits client/game.ts used
// to hard-code (aim labels, predicted hit markers, boss bar, objective lines, sounds).

import * as THREE from 'three';
import { BossEv, HEAD_NAMES, Part, PartHit, bossRaycast } from '../../../../sim/boss';
import { BossKind } from '../../../../sim/bosses/types';
import type { CerberusNet } from '../../../../sim/bosses/cerberus';
import { EFlag } from '../../../../sim/protocol';
import { BossView } from '../boss';
import type { BossAim, BossClient, BossClientFactory, BossCtx, BossFrame } from './types';
import type { HudBoss, HudObjective } from '../../contracts';

const PIPS = ['FUR', 'DYN', 'MAG'];

class CerberusClient implements BossClient {
  readonly kind = BossKind.Cerberus;
  readonly title = 'CERBERUS';
  readonly sub = 'THE JUNKYARD WARDEN';
  private view: BossView;
  private part: PartHit = { t: -1, part: Part.None, head: -1 };
  private v1 = new THREE.Vector3();
  private v3 = new THREE.Vector3();

  constructor(private ctx: BossCtx) {
    this.view = new BossView(ctx.scene, ctx.time, ctx.prewarm && ((o) => ctx.prewarm!(o)));
  }

  update(f: BossFrame | null, time: number, dt: number) {
    const e = f?.primary;
    if (f && e) {
      const b = f.net as CerberusNet;
      this.view.update({ x: e.x, y: e.y, z: e.z, yaw: e.yaw, st: e.st, prog: e.prog, hp: e.hp, aimX: e.aimX, aimZ: e.aimZ, hitAge: f.hitAge(e.id), net: b }, dt, time, f.ground);
      // the howl tell: the visor shakes as it fills its lungs
      if (b.atk === 6 && e.st === 1) this.ctx.shake(0.03 + 0.08 * e.prog);
    } else this.view.update(null, dt, time, f ? f.ground : () => 0);
  }

  bodyDied(_type: number, _x: number, _y: number, _z: number, time: number) { this.view.died(time); }

  onEvent(kind: number, a: number, _b: number, _eid: number, f: BossFrame | null, time: number) {
    const fx = this.ctx.fx(), sfx = this.ctx.sfx;
    const e = f?.primary;
    const pos = e ? { x: e.x, y: e.y + 12, z: e.z } : undefined;
    if (kind === BossEv.Intro) {
      if (a === 1) {
        // it lands: the slam
        sfx.play('explosion', pos, { pitch: 0.6 }); sfx.play('cerberus_roar', pos);
        if (pos) { fx?.explosion(this.v1.set(pos.x, pos.y - 12, pos.z), 9, 1); fx?.dust(this.v1, 1); }
        this.ctx.shake(0.9); this.ctx.rumble(1, 0.8, 400);
      }
    } else if (kind === BossEv.Attack) { if (a === 6 || a === 1) sfx.play('cerberus_roar', pos, { pitch: a === 6 ? 0.8 : 1.1 }); }
    else if (kind === BossEv.HeadBreak) { sfx.play('boss_phase'); if (pos) fx?.explosion(this.v1.set(pos.x, pos.y + 4, pos.z), 5, 1); this.ctx.shake(0.5); }
    else if (kind === BossEv.Ruff || kind === BossEv.Phase3) { sfx.play('boss_phase'); sfx.play('cerberus_roar', pos, { pitch: 0.9 }); }
    else if (kind === BossEv.RuffBreak) { sfx.play('shield_break'); this.ctx.flash(0.6); this.ctx.shake(0.5); }
    else if (kind === BossEv.Howl) {
      this.ctx.shake(1); this.ctx.chroma(1); this.ctx.rumble(1, 1, 450);
      sfx.play('cerberus_roar', pos, { pitch: 0.65, volume: 1 });
    } else if (kind === BossEv.CoreOpen) sfx.play('prime', pos, { pitch: 0.6 });
    else if (kind === BossEv.Dead) {
      this.view.died(time);
      sfx.play('cerberus_roar', pos, { pitch: 0.55 });
      for (let i = 0; i < 6; i++) setTimeout(() => {
        const p = this.view.cabPos(this.v3);
        if (p) { this.ctx.fx()?.explosion(p.clone().add(new THREE.Vector3((Math.random() - 0.5) * 9, (Math.random() - 0.3) * 8, (Math.random() - 0.5) * 9)), 5, 1); sfx.play('explosion', p); }
        this.ctx.shake(0.5);
      }, 250 + i * 380);
      this.ctx.flash(0.8);
    }
  }

  ray(f: BossFrame, ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number, ap: number, out: BossAim) {
    out.t = -1; out.kind = 0; out.head = false; out.label = '';
    const e = f.primary, b = f.net as CerberusNet;
    if (!e || e.st === 5) return out;
    const bh = bossRaycast(e.x, e.y, e.z, e.yaw, b.mouths & ~b.broken, b.core, ox, oy, oz, dx, dy, dz, maxT, this.part);
    if (bh.t < 0) return out;
    const weak = bh.part === Part.Mouth || bh.part === Part.Core;
    const hyper = (e.flags & EFlag.Hyper) !== 0;
    out.t = bh.t;
    out.kind = hyper ? 4 : weak ? 3 : bh.part === Part.Tag ? 2 : bh.part === Part.Head ? (ap >= 2 ? 2 : 4) : ap >= 3 ? 2 : 4;
    out.head = weak;
    out.label = bh.part === Part.Mouth ? `JAW · ${HEAD_NAMES[bh.head]} · CRIT` : bh.part === Part.Core ? 'FURNACE CORE · CRIT' : bh.part === Part.Tag ? 'COLLAR TAG · WEAK'
      : bh.part === Part.Head ? ((b.broken >> bh.head) & 1 ? `${HEAD_NAMES[bh.head]} · BROKEN` : `${HEAD_NAMES[bh.head]} HEAD`) : 'ARMOR PLATE · AV3';
    return out;
  }

  hud(f: BossFrame, aimLabel: string, o: HudBoss) {
    const b = f.net as CerberusNet, e = f.primary;
    if (!e || e.st === 5) return null;
    o.name = this.title; o.sub = this.sub; o.count = '';
    o.hp = e.hp;
    if (o.pips.length !== 3) o.pips = PIPS.map((label) => ({ label, frac: 1, broken: false, open: false }));
    const open = b.mouths & ~b.broken;
    for (let i = 0; i < 3; i++) { const p = o.pips[i]; p.frac = b.heads[i]; p.broken = ((b.broken >> i) & 1) === 1; p.open = ((open >> i) & 1) === 1; }
    o.hyper = (b.phase & 8) !== 0; o.meter = 'STATIC RUFF'; o.crack = b.crack;
    o.note = b.core ? 'FURNACE CORE EXPOSED' : '';
    o.warn = b.atk === 6 && e.st === 1 ? 'TAKE COVER' : '';
    o.aimPart = aimLabel;
    return o;
  }

  objective(f: BossFrame | null, o: HudObjective) {
    const b = f ? (f.net as CerberusNet) : null;
    o.title = 'PUT DOWN CERBERUS';
    const howl = b && b.atk === 6 && f?.primary?.st === 1;
    if (howl) { o.title = 'TAKE COVER'; o.detail = 'BREAK ITS LINE OF SIGHT'; o.tone = 2; }
    else if (b && (b.phase & 8)) o.detail = 'STATIC RUFF: PRIME IT, THEN DETONATE';
    else if (b && b.core) { o.detail = 'SHOOT THE FURNACE CORE'; o.tone = 1; }
    else if (b && b.mouths & ~b.broken) { o.detail = 'SHOOT THE OPEN JAW'; o.tone = 1; }
    else o.detail = 'OPEN JAWS AND COLLAR TAGS ARE WEAK';
  }

  dispose() { this.view.dispose(); }
}

export const cerberusClient: BossClientFactory = (ctx) => new CerberusClient(ctx);
